import { router } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useSetAtom } from "jotai/react";
import { useForm } from "react-hook-form";

import { CoverageCheckForm } from "@/blocks/CoverageCheckForm.tsx";
import { ScreenLayout } from "@/blocks/ScreenLayout.tsx";
import { VnBox } from "@/components/VnBox.tsx";
import { VnButton } from "@/components/VnButton/VnButton.tsx";
import { VnForm } from "@/components/VnForm.tsx";
import { VnScrollView } from "@/components/VnScrollView";
import { VnText } from "@/components/VnText/VnText.tsx";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations.ts";
import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries.ts";
import {
  coverageCheckFormSchema,
  CoverageCheckFormType,
} from "@/domain/householdMember.ts";
import { hasCompletedRegistrationAtom } from "@/stores/atoms";

export const RegistrationCoverageCheckScreen = () => {
  const { data: householdMembers } = useListHouseholdMembersQuery();
  const currentHouseholdMember = householdMembers?.[0];
  const { mutate: updateHouseholdMember } = useUpdateHouseholdMemberMutation();
  const setHasCompletedRegistration = useSetAtom(hasCompletedRegistrationAtom);

  const form = useForm<CoverageCheckFormType>({
    defaultValues: {
      ssn: "",
      medicaidSubscriberId: "",
    },
    mode: "onChange",
    resolver: standardSchemaResolver(coverageCheckFormSchema),
  });

  const { formState } = form;

  const handleSubmit = (formData: CoverageCheckFormType) => {
    if (!currentHouseholdMember?.id) {
      return;
    }

    updateHouseholdMember(
      {
        id: currentHouseholdMember.id,
        householdMemberData: {
          ssn: formData.ssn,
          medicaidSubscriberId: formData.medicaidSubscriberId,
        },
      },
      {
        onSuccess: () => {
          setHasCompletedRegistration(true);
          router.navigate("/(authed)");
        },
      },
    );
  };

  const handleSkip = () => {
    setHasCompletedRegistration(true);
    router.navigate("/(authed)");
  };

  return (
    <VnBox className="bg-background flex-1">
      <VnScrollView>
        <VnBox className="border-border bg-secondary mx-auto my-6 w-[90%] max-w-lg rounded-lg border px-2 py-5">
          <ScreenLayout
            variant="FORM"
            backgroundClassName="bg-secondary"
            hasHorizontalPadding={false}>
            <VnBox className="px-4">
              <VnText
                variant="headerTwo"
                i18nKey="native:registrationCoverageScreen.title"
              />
              <VnText
                variant="lg"
                className="text-muted-foreground mb-6 mt-2"
                i18nKey="native:registrationCoverageScreen.description"
              />
              <VnForm form={form} isInputOnlyForm>
                <CoverageCheckForm />
                <VnButton
                  className="container mx-auto w-full"
                  i18nKey="common:next"
                  disabled={!formState.isDirty || !formState.isValid}
                  onPress={() => {
                    void form.handleSubmit(handleSubmit)();
                  }}
                />
                <VnButton
                  className="container"
                  variant="secondary"
                  i18nKey="common:skip"
                  onPress={() => {
                    handleSkip();
                  }}
                />
              </VnForm>
            </VnBox>
          </ScreenLayout>
        </VnBox>
      </VnScrollView>
    </VnBox>
  );
};
